import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { CardFilters, StoredEntity } from './types';

type StoredEntitiesMap = Record<string, StoredEntity[]>;
type UseStoredEntitiesOptions = {
  entityKeyOverride?: string;
};

/**
 * Why create a custom hook for preferences
 *
 * We want better control over the preference state. Updating the preference state does not immediately
 * reflect in the hook returned value, which can lead to stale data issues. By creating a custom hook
 * that manages local state alongside the preference, we can ensure that our components always have
 * access to the most up-to-date data.
 */
const useStoredEntitiesState = () => {
  const [storedEntitiesPreference, setStoredEntitiesPreference] = usePreference('TeamPresenceStoredEntities');
  const [storedEntities, setStoredEntities] = useState<StoredEntitiesMap>(storedEntitiesPreference || {});

  // Sync local state with preference (handles external changes)
  useEffect(() => {
    if (storedEntitiesPreference) {
      setStoredEntities(storedEntitiesPreference);
    }
  }, [storedEntitiesPreference]);

  // Wrapper that updates both local state AND preference
  const updateStoredEntities = useCallback(
    (newValue: StoredEntitiesMap | ((prev: StoredEntitiesMap) => StoredEntitiesMap)) => {
      if (typeof newValue === 'function') {
        setStoredEntities((prev) => {
          const updated = newValue(prev);
          setStoredEntitiesPreference(updated); // Persist to preference
          return updated;
        });
      } else {
        setStoredEntities(newValue);
        setStoredEntitiesPreference(newValue); // Persist to preference
      }
    },
    [setStoredEntitiesPreference]
  );

  return [storedEntities, updateStoredEntities] as const;
};

export const useTeamPresenceLocationIds = () => {
  const { selectedLocationIds } = useAppScopeStore();
  return useMemo(
    () =>
      filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.PHONE_TEAM_PRESENCE_VIEW,
      }),
    [selectedLocationIds]
  );
};

export const useStoredEntities = (options?: UseStoredEntitiesOptions) => {
  const { locationData } = useAppScopeStore();
  const teamPresenceLocationIds = useTeamPresenceLocationIds();
  const [storedEntities, setStoredEntities] = useStoredEntitiesState();
  const lastRemovedEntityRef = useRef<StoredEntity | null>(null);

  const getEntityKey = useCallback(
    (locationIds: string[]) => {
      const tenantSet = new Set<string>();
      locationIds.forEach((locId) => {
        const location = locationData[locId];
        if (location?.phoneTenantId) {
          tenantSet.add(location.phoneTenantId);
        }
      });
      return Array.from(tenantSet).sort().join('|') || 'default';
    },
    [locationData]
  );

  /**
   * The entityKey uniquely identifies the set of phone tenants. We will use this key to store/retrieve entities.
   *
   * Unlike unify locations, databeta locations will have different keys depending on teamPresenceLocationIds, because each location has a different phone tenant.
   * This means that users will have a new "view" for every permutation of selected locations that map to different phone tenants.
   */
  const entityKeyOverride = options?.entityKeyOverride;
  const entityKey = useMemo(
    () => (entityKeyOverride ? entityKeyOverride : getEntityKey(teamPresenceLocationIds)),
    [entityKeyOverride, getEntityKey, teamPresenceLocationIds]
  );
  const hasEntityKey = useMemo(() => {
    if (!entityKey) return false;
    return Object.hasOwn(storedEntities ?? {}, entityKey);
  }, [storedEntities, entityKey]);

  // Get entities for the current entityKey and filter based on selected location IDs
  const entities = useMemo(
    () =>
      ((storedEntities || {})[entityKey] || []).filter((entity) => {
        return (
          entity.locationIds.length === 0 ||
          entity.locationIds.some((locationId) => teamPresenceLocationIds.includes(locationId))
        );
      }),
    [storedEntities, entityKey, teamPresenceLocationIds]
  );

  const addEntities = useCallback(
    (newEntities: Omit<StoredEntity, 'addedAt'>[]) => {
      const existingEntities = (storedEntities || {})[entityKey] || [];
      const existingMap = new Map(existingEntities.map((e) => [e.id, e]));

      newEntities.forEach((newEntity) => {
        if (!existingMap.has(newEntity.id)) {
          existingMap.set(newEntity.id, {
            ...newEntity,
            addedAt: new Date().toISOString(),
          });
        }
        // Existing entities are automatically preserved with original addedAt
      });

      const reconciledEntities = Array.from(existingMap.values());
      setStoredEntities({ ...(storedEntities || {}), [entityKey]: reconciledEntities });
    },
    [storedEntities, entityKey, setStoredEntities]
  );

  const removeEntity = useCallback(
    (id: string) => {
      const existingEntities = (storedEntities || {})[entityKey] || [];
      const entityToRemove = existingEntities.find((entity) => entity.id === id);

      if (entityToRemove) {
        lastRemovedEntityRef.current = entityToRemove;
      }

      const filteredEntities = existingEntities.filter((entity) => entity.id !== id);
      setStoredEntities({ ...(storedEntities || {}), [entityKey]: filteredEntities });
    },
    [storedEntities, entityKey, setStoredEntities]
  );

  const undoRemoveEntity = useCallback(() => {
    if (!lastRemovedEntityRef.current) return;

    setStoredEntities((currentStoredEntities) => {
      const existingEntities = (currentStoredEntities || {})[entityKey] || [];
      const entityToRestore = lastRemovedEntityRef.current;

      if (!entityToRestore) return currentStoredEntities;

      // Check if entity doesn't already exist
      if (existingEntities.some((e) => e.id === entityToRestore.id)) {
        return currentStoredEntities;
      }

      const updatedEntities = [...existingEntities, entityToRestore];
      return { ...(currentStoredEntities || {}), [entityKey]: updatedEntities };
    });

    lastRemovedEntityRef.current = null;
  }, [entityKey, setStoredEntities]);

  const removeEntities = useCallback(
    (ids: string[]) => {
      const existingEntities = (storedEntities || {})[entityKey] || [];
      const idSet = new Set(ids);
      const filteredEntities = existingEntities.filter((entity) => !idSet.has(entity.id));
      setStoredEntities({ ...(storedEntities || {}), [entityKey]: filteredEntities });
    },
    [storedEntities, entityKey, setStoredEntities]
  );

  const clearAllEntities = useCallback(() => {
    setStoredEntities({});
  }, [setStoredEntities]);

  const replaceEntities = useCallback(
    (newEntities: (StoredEntity | Omit<StoredEntity, 'addedAt'>)[]) => {
      const existingEntities = (storedEntities || {})[entityKey] || [];
      const existingMap = new Map(existingEntities.map((e) => [e.id, e]));

      const reconciledEntities = newEntities.map((newEntity) => {
        const existing = existingMap.get(newEntity.id);
        return {
          ...newEntity,
          addedAt: existing?.addedAt ?? new Date().toISOString(),
        };
      });

      setStoredEntities({ ...(storedEntities || {}), [entityKey]: reconciledEntities });
    },
    [storedEntities, entityKey, setStoredEntities]
  );

  const isPinned = useCallback((id: string) => entities.some((e) => e.id === id && e.isPinned), [entities]);

  const entityExists = useCallback((id: string) => entities.some((e) => e.id === id), [entities]);

  const pinEntity = useCallback(
    (id: string) => {
      setStoredEntities((prev) => ({
        ...prev,
        [entityKey]: ((prev || {})[entityKey] || []).map((entity) =>
          entity.id === id ? { ...entity, isPinned: true } : entity
        ),
      }));
    },
    [entityKey, setStoredEntities]
  );

  const unpinEntity = useCallback(
    (id: string) => {
      setStoredEntities((prev) => ({
        ...prev,
        [entityKey]: ((prev || {})[entityKey] || []).map((entity) =>
          entity.id === id ? { ...entity, isPinned: false } : entity
        ),
      }));
    },
    [entityKey, setStoredEntities]
  );

  return {
    entities,
    addEntities,
    removeEntity,
    removeEntities,
    undoRemoveEntity,
    clearAllEntities,
    replaceEntities,
    getEntityKey,
    entityKey,
    hasEntityKey,
    isPinned,
    entityExists,
    pinEntity,
    unpinEntity,
  };
};

export const useCardFiltersState = () => {
  const [filtersPreference, setFiltersPreference] = usePreference('TeamPresenceFilters');
  const defaultFilters: CardFilters = {
    location: [],
    deviceState: [],
    cardType: [],
    userStatus: [],
    search: '',
    sortBy: 'recent',
  };

  const [filters, setFilters] = useState<CardFilters>(filtersPreference || defaultFilters);

  // Sync local state with preference
  useEffect(() => {
    if (filtersPreference) {
      setFilters(filtersPreference as CardFilters);
    }
  }, [filtersPreference]);

  // Wrapper that updates both local state AND preference
  const updateFilters = useCallback(
    (newValue: CardFilters | ((prev: CardFilters) => CardFilters)) => {
      if (typeof newValue === 'function') {
        setFilters((prev) => {
          const updated = newValue(prev);
          setFiltersPreference(updated);
          return updated;
        });
      } else {
        setFilters(newValue);
        setFiltersPreference(newValue);
      }
    },
    [setFiltersPreference]
  );

  return [filters, updateFilters, defaultFilters] as const;
};
